// PA tool manifest — schemas, validation, and re-exports (PA-2a / PA-2b).

import {
  validateObligationCreate,
  validateObligationLookup,
} from "./paObligationToolValidate.js";
import {
  validateLeadDraft,
  validateServiceRequestDraft,
} from "./paDraftToolValidate.js";
import { isoDateTime, nonEmptyString, uuidString } from "./paToolHelpers.js";
import {
  formatPaToolManifestForPrompt as formatManifest,
  toOpenAiPaToolDefinitions as toOpenAiDefs,
} from "./paToolOpenAi.js";

export type PaDeepLinkMode =
  | "leads"
  | "serviceBoard"
  | "knowledgebase"
  | "operations"
  | "opsObligations"
  | "dailyChecklist"
  | "dashboard"
  | "training"
  | "aiPrompting";

export const PA_TOOL_IDS = [
  "note_append",
  "notification_schedule",
  "notification_send",
  "checklist_nudge",
  "task_create",
  "deep_link",
  "service_request_draft",
  "lead_draft",
  "obligation_lookup",
  "obligation_create",
] as const;

export type PaToolId = (typeof PA_TOOL_IDS)[number];

export type PaProposedToolStatus = "pending" | "confirmed" | "cancelled" | "executed" | "failed";

export type PaProposedTool = {
  proposalId: string;
  toolId: PaToolId;
  summary: string;
  arguments: Record<string, unknown>;
  confirmationRequired: boolean;
  status: PaProposedToolStatus;
};

export type PaNoteAppendArgs = { body: string };
export type PaNotificationScheduleArgs = {
  title: string;
  body?: string;
  due_at: string;
  assignee_user_id?: string;
};
export type PaNotificationSendArgs = {
  title: string;
  body: string;
  recipient_user_ids: string[];
};
export type PaChecklistNudgeArgs = {
  title: string;
  body?: string;
};
export type PaTaskCreateArgs = {
  title: string;
  body?: string;
  assignee_user_id: string;
  due_at?: string;
};
export type PaDeepLinkArgs = {
  target_mode: string;
  highlight_target?: string;
};
export type PaObligationLookupArgs = {
  query?: string;
  kind?: string;
  utility_subtype?: string;
  location_query?: string;
  obligation_id?: string;
  status?: string;
};
export type PaObligationCreateArgs = {
  kind: string;
  utility_subtype?: string;
  location_query?: string;
  org_wide?: boolean;
  provider_name?: string;
  account_ref?: string;
  description?: string;
  payment_url?: string;
  cadence?: string;
  due_rule?: string;
  amount_cents?: number;
  responsible_party?: string;
  notes?: string;
  status?: string;
};

export type PaToolValidationResult =
  | { ok: true; toolId: PaToolId; args: Record<string, unknown> }
  | { ok: false; error: string };

const DEEP_LINK_MODES: readonly PaDeepLinkMode[] = [
  "leads",
  "serviceBoard",
  "knowledgebase",
  "operations",
  "opsObligations",
  "dailyChecklist",
  "dashboard",
  "training",
  "aiPrompting",
];

export function isPaDeepLinkMode(value: string): value is PaDeepLinkMode {
  return (DEEP_LINK_MODES as readonly string[]).includes(value);
}

export function isPaToolId(value: unknown): value is PaToolId {
  return typeof value === "string" && (PA_TOOL_IDS as readonly string[]).includes(value);
}

export function validatePaToolArguments(
  toolId: PaToolId,
  raw: unknown,
): PaToolValidationResult {
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) {
    return { ok: false, error: "arguments must be an object" };
  }
  const obj = raw as Record<string, unknown>;

  switch (toolId) {
    case "note_append": {
      const body = nonEmptyString(obj.body);
      if (!body) return { ok: false, error: "body is required" };
      return { ok: true, toolId, args: { body: body.slice(0, 8000) } };
    }
    case "notification_schedule": {
      const title = nonEmptyString(obj.title);
      const due_at = isoDateTime(obj.due_at);
      if (!title) return { ok: false, error: "title is required" };
      if (!due_at) return { ok: false, error: "due_at must be a valid ISO datetime" };
      const assignee = obj.assignee_user_id != null ? uuidString(obj.assignee_user_id) : null;
      if (obj.assignee_user_id != null && !assignee) {
        return { ok: false, error: "assignee_user_id must be a valid uuid" };
      }
      return {
        ok: true,
        toolId,
        args: {
          title: title.slice(0, 500),
          body: typeof obj.body === "string" ? obj.body.slice(0, 8000) : "",
          due_at,
          ...(assignee ? { assignee_user_id: assignee } : {}),
        },
      };
    }
    case "notification_send": {
      const title = nonEmptyString(obj.title);
      const body = nonEmptyString(obj.body);
      const recipientIds = Array.isArray(obj.recipient_user_ids)
        ? [...new Set(obj.recipient_user_ids.map((x) => uuidString(x)).filter(Boolean) as string[])]
        : [];
      if (!title) return { ok: false, error: "title is required" };
      if (!body) return { ok: false, error: "body is required" };
      if (recipientIds.length === 0) {
        return { ok: false, error: "recipient_user_ids must include at least one valid user id" };
      }
      return {
        ok: true,
        toolId,
        args: {
          title: title.slice(0, 500),
          body: body.slice(0, 8000),
          recipient_user_ids: recipientIds,
        },
      };
    }
    case "checklist_nudge": {
      const title = nonEmptyString(obj.title);
      if (!title) return { ok: false, error: "title is required" };
      const body = typeof obj.body === "string" ? obj.body.trim().slice(0, 8000) : "";
      return {
        ok: true,
        toolId,
        args: {
          title: title.slice(0, 500),
          ...(body ? { body } : {}),
        },
      };
    }
    case "task_create": {
      const title = nonEmptyString(obj.title);
      const assignee_user_id = uuidString(obj.assignee_user_id);
      if (!title) return { ok: false, error: "title is required" };
      if (!assignee_user_id) return { ok: false, error: "assignee_user_id is required" };
      const due_at = obj.due_at != null ? isoDateTime(obj.due_at) : null;
      if (obj.due_at != null && !due_at) {
        return { ok: false, error: "due_at must be a valid ISO datetime when provided" };
      }
      return {
        ok: true,
        toolId,
        args: {
          title: title.slice(0, 500),
          body: typeof obj.body === "string" ? obj.body.slice(0, 8000) : "",
          assignee_user_id,
          ...(due_at ? { due_at } : {}),
        },
      };
    }
    case "deep_link": {
      const target_mode = nonEmptyString(obj.target_mode);
      if (!target_mode || !isPaDeepLinkMode(target_mode)) {
        return { ok: false, error: `target_mode must be one of: ${DEEP_LINK_MODES.join(", ")}` };
      }
      const highlight = nonEmptyString(obj.highlight_target);
      return {
        ok: true,
        toolId,
        args: {
          target_mode,
          ...(highlight ? { highlight_target: highlight.slice(0, 200) } : {}),
        },
      };
    }
    case "service_request_draft":
      return validateServiceRequestDraft(obj);
    case "lead_draft":
      return validateLeadDraft(obj);
    case "obligation_lookup":
      return validateObligationLookup(obj);
    case "obligation_create":
      return validateObligationCreate(obj);
    default:
      return { ok: false, error: "unknown tool" };
  }
}

export function paToolRequiresConfirmation(toolId: PaToolId): boolean {
  return toolId !== "obligation_lookup";
}

export function buildPaToolSummary(toolId: PaToolId, args: Record<string, unknown>): string {
  switch (toolId) {
    case "note_append":
      return `Save note: ${String(args.body ?? "").slice(0, 120)}`;
    case "notification_schedule":
      return `Schedule reminder “${args.title}” for ${args.due_at}`;
    case "notification_send":
      return `Send message “${args.title}” to ${(args.recipient_user_ids as string[] | undefined)?.length ?? 0} recipient(s)`;
    case "checklist_nudge":
      return `Org checklist nudge: “${args.title}” to all active members`;
    case "task_create":
      return `Create task “${args.title}” for assignee`;
    case "deep_link":
      return `Open ${args.target_mode} workspace`;
    case "service_request_draft":
      return `Draft service request: ${String(args.description ?? "").slice(0, 100)}`;
    case "lead_draft": {
      const name =
        String(args.customer_name ?? "").trim() ||
        [args.first_name, args.last_name].filter(Boolean).join(" ").trim() ||
        String(args.contact_query ?? "").trim() ||
        "new lead";
      return `Draft lead for ${name}`;
    }
    case "obligation_lookup": {
      const bits = [
        args.kind,
        args.utility_subtype,
        args.location_query,
        args.query,
        args.obligation_id ? "by id" : null,
      ].filter(Boolean);
      return `Look up obligations${bits.length ? `: ${bits.join(" / ")}` : ""}`;
    }
    case "obligation_create": {
      const where =
        args.org_wide === true
          ? "org-wide"
          : String(args.location_query ?? "").trim() || "unspecified location";
      const provider = String(args.provider_name ?? "").trim();
      return `Create ${args.kind} obligation at ${where}${provider ? ` (${provider})` : ""}`;
    }
    default:
      return "Proposed action";
  }
}

export function parsePaProposedTools(raw: unknown): PaProposedTool[] {
  if (!Array.isArray(raw)) return [];
  const out: PaProposedTool[] = [];
  for (const item of raw) {
    if (!item || typeof item !== "object") continue;
    const row = item as Record<string, unknown>;
    const toolId = row.toolId;
    const proposalId = nonEmptyString(row.proposalId);
    if (!isPaToolId(toolId) || !proposalId) continue;
    const validated = validatePaToolArguments(toolId, row.arguments ?? {});
    if (!validated.ok) continue;
    out.push({
      proposalId,
      toolId,
      summary: typeof row.summary === "string" ? row.summary : buildPaToolSummary(toolId, validated.args),
      arguments: validated.args,
      confirmationRequired: paToolRequiresConfirmation(toolId),
      status:
        row.status === "confirmed" ||
        row.status === "cancelled" ||
        row.status === "executed" ||
        row.status === "failed"
          ? row.status
          : "pending",
    });
  }
  return out;
}

export function formatPaToolManifestForPrompt(): string {
  return formatManifest();
}

export function toOpenAiPaToolDefinitions(): ReturnType<typeof toOpenAiDefs> {
  return toOpenAiDefs();
}
